/*
 * js-jm-cookie-banner.js: the cookie consent banner (snippets/jm-cookie-banner.liquid).
 * Ported from JOY Mobile Eilat (assets/cookie-banner.js there). That theme is ES modules; this
 * one is classic scripts, so the helpers it imported (define, focusables, announce) are local.
 *
 * The choice is recorded in Shopify's Customer Privacy API, which is what the store's tags read:
 * the Google & YouTube, Facebook & Instagram and TikTok apps load through Shopify's web pixels
 * (Settings › Customer events), not from this theme, and those pixels honour the visitor's
 * consent. A choice is saved locally only after Shopify confirms every optional category, and a
 * cleared Shopify acceptance is never replayed from localStorage. Close and Escape choose
 * essential only. The footer's "הגדרות עוגיות" opens the choice again.
 *
 * The region rules themselves (whether consent is required before tracking in Israel) are set in
 * Shopify admin, Settings › Customer privacy; showing a banner does not configure them.
 */
(function () {
  'use strict';

  var KEY = 'jm:cookie-consent';
  var KINDS = ['analytics', 'marketing', 'preferences'];
  var DAY = 864e5;

  /* consent-rules:start: the Node test reads the code between these markers. */
  function read() {
    try { return JSON.parse(localStorage.getItem(KEY)); } catch (e) { return null; }
  }
  function forget() {
    try { localStorage.removeItem(KEY); } catch (e) {}
  }
  function write(choice) {
    try { localStorage.setItem(KEY, JSON.stringify({ version: 2, choice: choice, at: Date.now() })); } catch (e) {}
  }

  var apiPromise = null;
  function readyApi() {
    var p = window.Shopify && window.Shopify.customerPrivacy;
    return p && typeof p.setTrackingConsent === 'function' && typeof p.currentVisitorConsent === 'function' ? p : null;
  }
  // Shopify's Customer Privacy API, loaded on demand. A blocked or late API resolves to null and
  // can be tried again later.
  function privacyApi(timeout) {
    timeout = timeout || 5000;
    if (readyApi()) return Promise.resolve(readyApi());
    if (!apiPromise) {
      apiPromise = new Promise(function (resolve) {
        var shopify = window.Shopify;
        if (!shopify || typeof shopify.loadFeatures !== 'function') return resolve(null);
        var timer = setTimeout(function () { resolve(null); }, timeout);
        try {
          shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
            clearTimeout(timer);
            resolve((!error && readyApi()) || null);
          });
        } catch (e) {
          clearTimeout(timer);
          resolve(null);
        }
      });
    }
    return apiPromise.then(function (result) {
      if (!result) apiPromise = null;
      return result;
    });
  }
  function consent(privacy) {
    try { return (privacy && privacy.currentVisitorConsent && privacy.currentVisitorConsent()) || {}; } catch (e) { return {}; }
  }
  function hasChosen(privacy) {
    var c = consent(privacy);
    return KINDS.every(function (k) { return c[k] === 'yes' || c[k] === 'no'; });
  }
  // Records the choice and resolves true only when Shopify confirms all three categories.
  // Accepting analytics and marketing is not a separate authorisation to sell or share data: a
  // previous opt-out of data sale is kept.
  function record(privacy, granted, timeout) {
    timeout = timeout || 7000;
    return new Promise(function (resolve) {
      if (!privacy || typeof privacy.setTrackingConsent !== 'function') return resolve(false);
      var timer = setTimeout(function () { resolve(false); }, timeout);
      var flags = { analytics: granted, marketing: granted, preferences: granted };
      if (!granted) flags.sale_of_data = false;
      else {
        var previous = consent(privacy).sale_of_data;
        if (previous === 'yes' || previous === 'no') flags.sale_of_data = previous === 'yes';
      }
      try {
        privacy.setTrackingConsent(flags, function (result) {
          clearTimeout(timer);
          var c = consent(privacy);
          resolve(!(result && result.error) && KINDS.every(function (k) { return c[k] === (granted ? 'yes' : 'no'); }));
        });
      } catch (e) {
        clearTimeout(timer);
        resolve(false);
      }
    });
  }
  /* consent-rules:end */

  window.jmCookieConsent = { record: record, privacyApi: privacyApi, KEY: KEY };

  if (!window.customElements || window.customElements.get('jm-cookie-banner')) return;

  var stylesPromise = null;
  function loadStyles(href) {
    if (!stylesPromise) {
      stylesPromise = new Promise(function (resolve) {
        if (!href) return resolve();
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', resolve, { once: true });
        setTimeout(resolve, 4000);
        document.head.appendChild(link);
      });
    }
    return stylesPromise;
  }

  function announce(message) {
    if (!message) return;
    var region = document.getElementById('JmCookieStatus');
    if (!region) {
      region = document.createElement('p');
      region.id = 'JmCookieStatus';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    region.textContent = '';
    setTimeout(function () { region.textContent = message; }, 100);
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function nativeBanner() {
    return document.getElementById('shopify-pc__banner');
  }

  class JmCookieBanner extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.jm-cookie__panel');
      this.error = this.querySelector('[data-consent-error]');
      this.resize = typeof ResizeObserver === 'function' ? new ResizeObserver(() => this.offset()) : null;
      this.addEventListener('click', this);
      this.addEventListener('keydown', this);
      document.addEventListener('visitorConsentCollected', this);
      window.addEventListener('storage', this);
      this.openPreferences = (event) => {
        const button = event.target.closest && event.target.closest('[data-cookie-preferences]');
        if (!button) return;
        event.preventDefault();
        this.returnFocus = button;
        this.show(true);
      };
      document.addEventListener('click', this.openPreferences);
      if (window.Shopify && window.Shopify.designMode) return;
      this.decide();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      this.removeEventListener('keydown', this);
      document.removeEventListener('visitorConsentCollected', this);
      document.removeEventListener('click', this.openPreferences);
      window.removeEventListener('storage', this);
      if (this.resize) this.resize.disconnect();
      if (this.watch) this.watch.disconnect();
      this.offset(0);
    }

    handleEvent(event) {
      if (event.type === 'click') {
        const button = event.target.closest && event.target.closest('[data-consent]');
        if (button) this.choose(button.getAttribute('data-consent'));
      } else if (event.type === 'keydown' && event.key === 'Escape' && !this.hidden) {
        event.preventDefault();
        this.choose('decline');
      } else if (event.type === 'visitorConsentCollected' && !this.busy && hasChosen(window.Shopify && window.Shopify.customerPrivacy)) {
        this.hide();
      } else if (event.type === 'storage' && event.key === KEY) {
        const saved = this.stored();
        if (saved && saved.choice === 'decline') return this.revokeFromAnotherTab();
        this.decide();
      }
    }

    async revokeFromAnotherTab() {
      if (this.busy) return;
      const ok = await record(await privacyApi(), false);
      if (ok) this.hide();
      else {
        await this.show();
        this.error.hidden = false;
        this.offset();
      }
    }

    // The local record, when it is version 2, a known choice, and younger than the renewal period.
    stored() {
      const saved = read();
      if (!saved || saved.version !== 2 || (saved.choice !== 'accept' && saved.choice !== 'decline')) return null;
      const age = Date.now() - saved.at;
      const days = Number(this.dataset.renewDays) || 365;
      return Number.isFinite(saved.at) && age >= 0 && age < days * DAY ? saved : null;
    }

    async decide() {
      const privacy = await privacyApi();
      if (!this.isConnected) return;
      const previous = read();
      const saved = this.stored();
      if (saved && saved.choice === 'decline') {
        const c = consent(privacy);
        if (KINDS.every((k) => c[k] === 'no') && c.sale_of_data === 'no') return this.hide();
        if (await record(privacy, false)) return this.hide();
        await this.show();
        this.error.hidden = false;
        this.offset();
        return;
      }
      if (saved && hasChosen(privacy)) return this.hide();
      // An old or missing Shopify acceptance must be given again, never replayed from localStorage.
      if (previous) forget();
      if (!previous && hasChosen(privacy)) return this.hide();
      if (this.dataset.audience === 'required' && privacy && typeof privacy.shouldShowBanner === 'function' && !privacy.shouldShowBanner()) return;
      if (nativeBanner()) return;
      if (typeof MutationObserver === 'function') {
        this.watch = new MutationObserver(() => nativeBanner() && this.hide());
        this.watch.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => this.watch && this.watch.disconnect(), 15000);
      }
      this.show();
    }

    async show(focus) {
      await loadStyles(this.dataset.stylesheet);
      if (!this.isConnected) return;
      this.hidden = false;
      this.error.hidden = true;
      if (this.resize) this.resize.observe(this.panel);
      this.offset();
      if (focus) {
        const decline = this.querySelector('[data-consent="decline"]');
        if (decline) decline.focus();
      }
    }

    hide() {
      this.hidden = true;
      if (this.resize) this.resize.disconnect();
      if (this.watch) this.watch.disconnect();
      this.offset(0);
    }

    // Lifts the floating buttons above the banner while it shows (--jm-cookie-offset, read by
    // css-jm-a11y-widget.css and css-jm-whatsapp-fab.css), and keeps focused fields clear of it.
    offset(height) {
      if (height === undefined) height = this.hidden || !this.panel ? 0 : Math.ceil(this.panel.getBoundingClientRect().height);
      const body = document.body.style;
      const root = document.documentElement.style;
      if (height > 0) {
        body.setProperty('--jm-cookie-offset', height + 10 + 'px');
        root.setProperty('scroll-padding-block-end', height + 24 + 'px');
      } else {
        body.removeProperty('--jm-cookie-offset');
        root.removeProperty('scroll-padding-block-end');
      }
    }

    async choose(choice) {
      if (this.busy || (choice !== 'accept' && choice !== 'decline')) return;
      if (window.Shopify && window.Shopify.designMode) return this.hide();
      this.busy = true;
      this.error.hidden = true;
      if (this.panel) this.panel.setAttribute('aria-busy', 'true');
      const buttons = this.querySelectorAll('[data-consent]');
      buttons.forEach((b) => { b.disabled = true; });
      const ok = await record(await privacyApi(), choice === 'accept');
      this.busy = false;
      if (this.panel) this.panel.removeAttribute('aria-busy');
      buttons.forEach((b) => { b.disabled = false; });
      if (!this.isConnected) return;
      if (!ok) {
        this.error.hidden = false;
        this.offset();
        return;
      }
      write(choice);
      const active = document.activeElement;
      const keyboard = this.contains(active) && active.matches && active.matches(':focus-visible');
      this.hide();
      announce(this.dataset.saved);
      if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus();
      else if (keyboard) {
        const next = Array.prototype.find.call(document.body.querySelectorAll(FOCUSABLE), (el) => this.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
        if (next) next.focus();
      }
    }
  }

  window.customElements.define('jm-cookie-banner', JmCookieBanner);
})();
